import { CartAPI } from '../api/cart';
import { KEYS } from '../constants/keys';
import { setPreOrderButtonText, updateSellingPlanAllocation } from '../pre-order';
import { getTemplate } from '../template';
import { buildUrlWithLocalization, formatCurrency, setWindowLocation } from '../utils';
import { isKeyboardActivated } from '../utils/keyboardActivated';
import { getThemeSettings } from '../utils/settings';
import {
  focusFirstElement,
  restoreFocusedElement,
  saveFocusElement,
  trapFocus,
} from '../utils/wcag-modal';
import { getCartSelectorAndUpdateAction, updateCartCount } from './selectors';

/**
 * @type {import('./index.d').handleCart}
 */
export const handleCart = (context) => {
  const { enableCart } = getThemeSettings(context)?.cart || {};

  if (!enableCart) return;

  // Add class to body if cart is enabled to disable the default cart by css
  document.body.classList.add('boost-sd__cart-enabled');

  const [getCart, setCart] = context.useContextState('cart', {
    isShow: false,
  });

  const [getForceRender, setForceRender] = context.useContextState('force-render', false);
  const item = getCartSelectorAndUpdateAction();

  if (!item || item?.selector === null) console.error('Cart icon is not found');

  const cartIcon = document.querySelector(item?.selector);

  cartIcon?.addEventListener('click', (e) => {
    e.preventDefault();

    setCart({
      isShow: !getCart().isShow,
    });
  });

  const showCart = () => {
    setCart({ isShow: true });

    if (window.focusedElementBeforeMiniCart === null) {
      window.focusedElementBeforeModalCart = document.activeElement;
    }

    // Use MutationObserver to focus when the modal is fully rendered
    const observer = new MutationObserver((mutations, obs) => {
      const modal = document.getElementById('boost-sd__modal-cart');
      if (modal) {
        focusFirstElement(modal, 'a[href], button:not([disabled]), input');
        const handleTrapFocus = (e) => {
          trapFocus(
            e,
            modal,
            'a[href], button:not([disabled]), input',
            '.boost-sd__modal-close-btn'
          );
        };

        modal.addEventListener('keydown', handleTrapFocus);

        const closeBtn = modal?.querySelector('.boost-sd__modal-close-btn');
        closeBtn?.removeEventListener('click', hideCart); // Remove any previous listeners
        closeBtn?.addEventListener('click', hideCart);

        closeBtn?.addEventListener('keydown', handleCloseBtnKeydown);

        obs.disconnect(); // Stop observing once the modal is rendered
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    function handleCloseBtnKeydown(e) {
      if (KEYS.includes(e.key)) {
        e.preventDefault();
        hideCart();
      }
    }
  };

  const hideCart = () => {
    setCart({ isShow: false });

    const modal = document.getElementById('boost-sd__modal-cart');
    if (modal) {
      modal.removeEventListener('keydown', trapFocus);
    }

    // Only return focus if the interaction was via keyboard
    if (isKeyboardActivated()) {
      if (window.focusedElementBeforeMiniCart) {
        window.focusedElementBeforeMiniCart.focus();
        window.focusedElementBeforeMiniCart = null;
      } else if (window.buttonQuickviewFocus) {
        window.buttonQuickviewFocus.focus();
        window.buttonQuickviewFocus = null;
      } else if (window.focusedElementBeforeModalCart) {
        window.focusedElementBeforeModalCart.focus();
        window.focusedElementBeforeModalCart = null;
      }
    }
  };

  const reRenderCart = () => {
    setForceRender(!getForceRender());
  };

  // Listen to custom event to open cart
  window.addEventListener('boost-sd-open-cart', showCart);
  window.addEventListener('boost-sd-close-cart', hideCart);
  window.addEventListener('boost-sd-re-render-cart', reRenderCart);

  // Add event listeners to the cart icon to open the modal on Space and Enter
  const handleCartIconKeydown = (e) => {
    if (KEYS.includes(e.key)) {
      e.preventDefault();
      window.focusedElementBeforeModalCart = document.activeElement;
      showCart();
    }
  };

  cartIcon?.addEventListener('keydown', handleCartIconKeydown);

  context.render(async () => {
    const cart = getCart();

    const { html, itemCount } = await renderCart(context);
    item?.action(itemCount);

    const initOnClickOutSide = (e) => {
      const closeBtn = modal?.querySelector('.boost-sd__modal-close-btn');
      const cb = () => {
        setCart({
          isShow: false,
        });
      };

      outsideClickListener(document.querySelector('.boost-sd__cart'), e, cb, closeBtn);
    };

    const modal = document.getElementById('boost-sd__modal-cart');
    if (cart && cart.isShow) {
      if (modal) {
        document.body.removeChild(modal);
      }

      document.body.insertAdjacentHTML('beforeend', html);
      document.body.classList.add('modal-open-cart');

      const cartItems = document.querySelectorAll('.boost-sd__cart-item');
      const viewCartBtn = document.querySelector('.boost-sd__btn-view-cart');
      const checkOutBtn = document.querySelector('.boost-sd__btn-checkout');

      const closeBtn = document.querySelector('.boost-sd__modal-close-btn');

      cartItems?.forEach((cartItem) => {
        const buttons = cartItem.querySelectorAll('.boost-sd__input-number-btn');
        const decreaseQuantityBtn = buttons?.[0];
        const increaseQuantityBtn = buttons?.[1];
        const quantityInput = cartItem.querySelector('.boost-sd__input-number-input');
        const removeItemBtn = cartItem.querySelector('.boost-sd__cart-item-remove-btn');
        const productId = cartItem.getAttribute('data-product-id');

        decreaseQuantityBtn?.addEventListener('click', async () => {
          saveFocusElement(decreaseQuantityBtn); // Save the focused element before the cart update
          await CartAPI.change(productId, Number(quantityInput.value) - 1);
          setForceRender(!getForceRender());
          restoreFocusedElement();
        });
        increaseQuantityBtn?.addEventListener('click', async () => {
          saveFocusElement(increaseQuantityBtn);
          await CartAPI.change(productId, Number(quantityInput.value) + 1);
          setForceRender(!getForceRender());
          restoreFocusedElement();
        });
        removeItemBtn?.addEventListener('click', async () => {
          saveFocusElement(removeItemBtn);
          await CartAPI.change(productId, 0);
          setForceRender(!getForceRender());
          restoreFocusedElement();
        });

        function onlyNumbers(evt) {
          // Prevent non-numeric input
          const charCode = evt?.which || evt?.keyCode;
          if (charCode > 31 && (charCode < 48 || charCode > 57)) {
            evt.preventDefault();
          }
        }

        quantityInput.addEventListener('keypress', (e) => {
          onlyNumbers(e);
        });

        quantityInput.addEventListener('change', async (e) => {
          const value = e.target.value;

          if (Number.isInteger(Number(value)) && Number(value) > 0) {
            saveFocusElement(quantityInput);
            await CartAPI.change(productId, Number(value));
            setForceRender(!getForceRender());
            restoreFocusedElement();
          }
        });
      });

      viewCartBtn?.addEventListener('click', () => {
        setWindowLocation(buildUrlWithLocalization('cart'));
      });

      checkOutBtn?.addEventListener('click', () => {
        setWindowLocation('/checkout');
      });

      closeBtn?.addEventListener('click', () => {
        hideCart();
      });

      document
        .getElementById('boost-sd__modal-cart')
        ?.addEventListener('click', initOnClickOutSide);
    } else {
      const closeBtn = document.querySelector('.boost-sd__modal-close-btn');

      if (closeBtn) {
        closeBtn.removeEventListener('click', closeCartModal);
      }
      document
        .getElementById('boost-sd__modal-cart')
        ?.removeEventListener('click', initOnClickOutSide);

      document.getElementById('boost-sd__modal-cart')?.remove();
      document.body.classList.remove('modal-open-cart');
    }
  }, ['cart', 'force-render']);
};

/**
 * @type {import('./index.d').addToCart}
 */
export const addToCart = async (context, item, target, multiple, onSuccess, onFail) => {
  const { productId, quantity = 1, selling_plan = null } = item;

  const ctaBtnText = target.querySelector('.boost-sd__cta-button-text, .boost-sd__btn-add-to-cart');
  const element = ctaBtnText || target;
  if (!element.classList.contains('boost-sd__button--icon')) {
    element.textContent =
      context.app?.translation?.productItem?.atcAddingToCartBtnLabel || 'Adding...';
  }
  element.setAttribute('disabled', 'true');

  const result = {
    status: '',
    response: null,
  };

  try {
    // multiple = true means add multi product to cart - RCM Bundle
    const res = multiple
      ? await CartAPI.addMultiProductToCart(item)
      : await CartAPI.add(productId, quantity, selling_plan);

    if (res.status === 200) {
      if (!element.classList.contains('boost-sd__button--icon')) {
        element.textContent =
          context.app?.translation?.productItem?.atcAddedToCartBtnLabel || 'Added!';
      }

      const { enableCart, autoOpenAjaxCartOnAddToCart = true } =
        getThemeSettings(context)?.cart || {};
      const { noneCartOption = false } = window.boostWidgetIntegration || {};
      const shouldOpenCart = !noneCartOption && autoOpenAjaxCartOnAddToCart;

      /**
       * Communicate through event because ATC action handler belong to ProductGridFilter or RCM context
       * State from Cart context can't access other contexts and trigger re-render
       */
      if (enableCart) {
        const event = shouldOpenCart ? 'boost-sd-open-cart' : 'boost-sd-re-render-cart';
        window.dispatchEvent(new CustomEvent(event));
      } else {
        const currentLocale = (window?.Shopify?.routes?.root || '/').replace(/\/$/, '');

        if (currentLocale === '') {
          setWindowLocation('/cart');
        } else {
          setWindowLocation(`${currentLocale}/cart`);
        }
      }

      if (onSuccess) setTimeout(onSuccess, 300);

      result.status = 'success';
    } else {
      element.textContent =
        context.app?.translation?.productItem?.atcFailedToCartBtnLabel || 'Failed!';
      if (onFail) setTimeout(onFail, 300);

      result.status = 'failed';
    }

    result.response = await res.json();
  } catch (error) {
    element.textContent =
      context.app?.translation?.productItem?.atcFailedToCartBtnLabel || 'Failed!';
    if (onFail) setTimeout(onFail, 300);

    result.status = 'failed';
  }

  element.removeAttribute('disabled');

  setTimeout(() => {
    if (!setPreOrderButtonText(element, context)) {
      if (!element.classList.contains('boost-sd__button--icon')) {
        element.textContent =
          context.app?.translation?.productItem?.atcAvailableLabel || 'Add to cart';
      }
    }
  }, 500);

  return result;
};

/**
 * @type {import('./index.d').outsideClickListener}
 */
const outsideClickListener = (element, event, callback, ignore) => {
  if (!element?.contains(event.target) && !ignore?.contains(event.target)) {
    callback(event);
  }
};

/**
 * @type {import('./index.d').closeCartModal}
 */
const closeCartModal = () => {
  document.getElementById('boost-sd__modal-cart')?.remove();
  document.body.classList.remove('modal-open-cart');
};

/** final_price: The price of the line item in the currency's subunit. This includes any line-level discounts.
 * The value is output in the customer's local (presentment) currency.
 * @type {import('./index.d').getFinalPrice}
 */
const getFinalPrice = (item) => {
  return (item.final_price || item.price || 0) / 100;
};

/**
 * @type {import('./index.d').renderCart}
 */
export const renderCart = async (context) => {
  const data = await CartAPI.getAll();

  const {
    cartStyle = 'side',
    generalLayout: { shape = 'round' },
  } = getThemeSettings(context)?.cart || {};

  let html = '';

  const { showCurrencyCodes } =
    context?.templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price || {};

  data?.items.forEach((item) => {
    item.price = formatCurrency({ context, value: getFinalPrice(item), showCurrencyCodes });
    item.options_with_values = item?.options_with_values?.filter((option) => {
      return option.name !== 'Title' && option.value !== 'Default Title';
    });
    // Update selling_plan when enable pre-order
    updateSellingPlanAllocation(item, context);
  });

  html += await context.templateRender(getTemplate(context).cartTemplate, {
    items: data.items,
    item_count: data.item_count,
    items_subtotal_price: formatCurrency({
      context,
      value: (data?.items_subtotal_price || 0) / 100,
      showCurrencyCodes,
    }),
    cartStyle,
    shape,
    locale: (window?.Shopify?.routes?.root || '/')?.replace(/\/$/, ''),
  });

  return {
    html,
    itemCount: data.item_count,
  };
};

const initCartPageEvents = async () => {
  await CartAPI.restoreCartIfNeeded();
  const updatedCart = await CartAPI.getAll();
  updateCartCount(updatedCart.item_count);
};

window.addEventListener('pageshow', initCartPageEvents);
